<template>
  <div>
    <div ref="dashboardContainer"></div>
    <div class="mt-3 flex justify-end">
      <BaseButton
        icon="paper-plane"
        color="primary"
        :title="$t('button.send')"
        :disabled="!hasFiles || uploading"
        :display-loader="uploading"
        @click.prevent="processSubmit"
      />
    </div>
  </div>
</template>

<script>
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'

import French from '@uppy/locales/lib/fr_FR'
import Dutch from '@uppy/locales/lib/nl_NL'
import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'

import apiClient from '@u/apiClient'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'

/**
 * Multi-file uploader (Uppy dashboard): each selected file is POSTed to `url`
 * as an `attachments` form field, then `confirm-submit` is emitted, as the
 * former FilePond component did.
 */
export default {
  name: 'BaseFileDropComponent',
  components: { BaseButton },
  props: {
    url: { type: String, required: true },
    acceptedFiles: {
      type: Array,
      required: false,
      default: () => [
        'image/jpeg',
        'image/png',
        'image/gif',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ]
    }
  },
  emits: ['confirm-submit'],
  data() {
    return { hasFiles: false, uploading: false }
  },
  mounted() {
    this.uppy = new Uppy({
      autoProceed: false,
      restrictions: { allowedFileTypes: this.acceptedFiles }
    }).use(Dashboard, {
      inline: true,
      hideUploadButton: true,
      width: '100%',
      height: 300,
      target: this.$refs.dashboardContainer,
      proudlyDisplayPoweredByUppy: false,
      locale: this.$i18n.locale === 'nl_BE' ? Dutch : French
    })
    const refresh = () => {
      this.hasFiles = this.uppy.getFiles().length > 0
    }
    this.uppy.on('file-added', refresh)
    this.uppy.on('file-removed', refresh)
  },
  beforeUnmount() {
    this.uppy?.destroy()
  },
  methods: {
    async processSubmit() {
      this.uploading = true
      try {
        for (const file of this.uppy.getFiles()) {
          const formData = new FormData()
          formData.append('attachments', file.data, file.name)
          await apiClient.post(this.url, formData)
          // Sent: out of the list, so a retry after a failure does not
          // upload it twice.
          this.uppy.removeFile(file.id)
        }
        this.$emit('confirm-submit')
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style>
/* Uppy's accent colour is blue: Crema's red instead. */
.uppy-Dashboard-browse,
.uppy-Dashboard-browse:hover {
  color: var(--color-primary-500);
}
</style>
